import { BACKEND_URL, FEATURE_FLAGS } from '../config/features';
import { metrics, Logger } from './metrics';
import { mirrorSessionRemoval } from '../mobile/session';

/**
 * Erro customizado para identificar falhas no backend
 */
export class BackendError extends Error {
  constructor(message, statusCode, originalError, endpoint) {
    super(message);
    this.name = 'BackendError';
    this.statusCode = statusCode;
    this.originalError = originalError;
    this.endpoint = endpoint;
    this.timestamp = new Date().toISOString();
    this.isRetryable = BackendError.isRetryableError(statusCode);
    this.isCritical = BackendError.isCriticalError(statusCode);
  }

  static isRetryableError(statusCode) {
    return [0, 408, 500, 502, 503, 504].includes(statusCode);
  }

  static isCriticalError(statusCode) {
    return [0, 408, 503, 504].includes(statusCode);
  }

  getUserFriendlyMessage() {
    if (this.statusCode === 0) {
      return 'Não foi possível conectar ao servidor. Verifique sua conexão.';
    }
    if (this.statusCode === 401) {
      return 'Sessão expirada. Por favor, faça login novamente.';
    }
    if (this.statusCode === 403) {
      return 'Você não tem permissão para executar esta ação.';
    }
    if (this.statusCode === 404) {
      return 'Recurso não encontrado.';
    }
    if (this.statusCode === 408) {
      return 'Operação demorou muito tempo. Tente novamente.';
    }
    if (this.statusCode >= 500) {
      return 'Erro no servidor. Estamos trabalhando para resolver.';
    }
    return this.message || 'Ocorreu um erro inesperado.';
  }
}

function getAuthToken() {
  return localStorage.getItem('correfood_token');
}

function clearStoredAuth() {
  localStorage.removeItem('correfood_token');
  localStorage.removeItem('correfood_user');
  void mirrorSessionRemoval(['correfood_token', 'correfood_user']);
}

function notifyAuthExpired(endpoint) {
  clearStoredAuth();
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('correfood:auth-expired', { detail: { endpoint } }));
  }
}

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

function canonicalizeLegacyLabel(value) {
  const label = String(value || '').trim();
  if (!label) return '';
  return label
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

function isLegacySitioLabel(value) {
  const canonical = canonicalizeLegacyLabel(value);
  return canonical === 'sitio' || canonical === 'satio' || canonical === 'stio';
}

function normalizeLegacyPayload(data) {
  if (Array.isArray(data)) {
    return data.map(normalizeLegacyPayload);
  }

  if (data && typeof data === 'object') {
    const normalized = {};
    Object.entries(data).forEach(([key, value]) => {
      const normalizedKey = isLegacySitioLabel(key) ? 'adicionais' : key;
      normalized[normalizedKey] = normalizeLegacyPayload(value);
    });
    return normalized;
  }

  if (typeof data === 'string' && isLegacySitioLabel(data)) {
    return 'Adicionais';
  }

  return data;
}

function extractErrorMessageFromDetail(detail) {
  if (!detail) return null;

  if (typeof detail === 'string') {
    const trimmed = detail.trim();
    return trimmed || null;
  }

  if (Array.isArray(detail)) {
    const first = detail[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
    if (first && typeof first === 'object') {
      const msg = typeof first.msg === 'string' ? first.msg.trim() : '';
      if (msg) return msg;
    }
    return null;
  }

  if (typeof detail === 'object') {
    const direct =
      (typeof detail.message === 'string' && detail.message.trim())
      || (typeof detail.detail === 'string' && detail.detail.trim())
      || (typeof detail.error === 'string' && detail.error.trim())
      || '';
    if (direct) return direct;
  }

  return null;
}

function resolveBackendErrorMessage(errorData) {
  if (!errorData || typeof errorData !== 'object') {
    return 'Erro ao comunicar com o servidor';
  }

  const fromDetail = extractErrorMessageFromDetail(errorData.detail);
  if (fromDetail) return fromDetail;

  if (typeof errorData.message === 'string' && errorData.message.trim()) {
    return errorData.message.trim();
  }

  return 'Erro ao comunicar com o servidor';
}

export async function backendRequest(endpoint, options = {}, attemptNumber = 1) {
  const startTime = Date.now();
  const method = options.method || 'GET';

  try {
    const token = getAuthToken();
    const accessCode = localStorage.getItem('correfood_access_code');

    if (!token && !options.skipAuth) {
      throw new BackendError('Usuário não autenticado', 401, null, endpoint);
    }

    const headers = {
      'Content-Type': 'application/json',
      ...options.headers
    };

    if (token && !options.skipAuth) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (accessCode) {
      headers['X-Access-Code'] = accessCode;
    }

    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, FEATURE_FLAGS.backendTimeout);

    const url = `${BACKEND_URL}${endpoint}`;

    const response = await fetch(url, {
      ...options,
      headers,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    const responseTime = Date.now() - startTime;

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      if (response.status === 401 && !options.skipAuth) {
        notifyAuthExpired(endpoint);
      }

      const resolvedErrorMessage = resolveBackendErrorMessage(errorData);
      const error = new BackendError(
        resolvedErrorMessage,
        response.status,
        errorData,
        endpoint
      );

      Logger.logBackendError(method, endpoint, error, responseTime);
      metrics.recordError(error.message);
      metrics.recordBackendRequest(endpoint, responseTime, false);

      throw error;
    }

    const data = normalizeLegacyPayload(await response.json());

    Logger.logBackendRequest(method, endpoint, responseTime);
    metrics.recordBackendRequest(endpoint, responseTime, true);

    return data;
  } catch (error) {
    const duration = Date.now() - startTime;

    if (error.name === 'AbortError') {
      Logger.logTimeout(endpoint, FEATURE_FLAGS.backendTimeout);

      const timeoutError = new BackendError(
        'Tempo limite de requisição excedido',
        408,
        error,
        endpoint
      );

      metrics.recordError('timeout');

      if (attemptNumber < FEATURE_FLAGS.maxRetries && timeoutError.isRetryable) {
        return handleRetry(endpoint, options, attemptNumber, timeoutError);
      }

      throw timeoutError;
    }

    if (error instanceof TypeError && error.message.includes('fetch')) {
      const networkError = new BackendError(
        'Erro de conexão com o servidor',
        0,
        error,
        endpoint
      );

      Logger.logBackendError(method, endpoint, networkError, duration);
      metrics.recordError('network');

      if (attemptNumber < FEATURE_FLAGS.maxRetries && networkError.isRetryable) {
        return handleRetry(endpoint, options, attemptNumber, networkError);
      }

      throw networkError;
    }

    if (error instanceof BackendError) {
      if (attemptNumber < FEATURE_FLAGS.maxRetries && error.isRetryable) {
        return handleRetry(endpoint, options, attemptNumber, error);
      }

      throw error;
    }

    const genericError = new BackendError(
      error.message || 'Erro desconhecido',
      500,
      error,
      endpoint
    );

    Logger.logBackendError(method, endpoint, genericError, duration);
    metrics.recordError('unknown');

    throw genericError;
  }
}

async function handleRetry(endpoint, options, attemptNumber) {
  const nextAttempt = attemptNumber + 1;

  Logger.logRetry(nextAttempt, FEATURE_FLAGS.maxRetries, endpoint);

  const delay = FEATURE_FLAGS.retryDelay * Math.pow(2, attemptNumber - 1);
  await sleep(delay);

  return backendRequest(endpoint, options, nextAttempt);
}

async function executeFallback(error) {
  throw new Error(error.getUserFriendlyMessage());
}

export async function backendGet(endpoint, options = {}) {
  try {
    return await backendRequest(endpoint, {
      ...options,
      method: 'GET'
    });
  } catch (error) {
    if (error instanceof BackendError && error.isCritical && FEATURE_FLAGS.enableAutoFallback) {
      return await executeFallback(error, endpoint, options.fallbackData);
    }
    throw error;
  }
}

export async function backendPost(endpoint, body, options = {}) {
  try {
    return await backendRequest(endpoint, {
      ...options,
      method: 'POST',
      body: JSON.stringify(body)
    });
  } catch (error) {
    if (error instanceof BackendError && error.isCritical && FEATURE_FLAGS.enableAutoFallback) {
      return await executeFallback(error, endpoint, body);
    }
    throw error;
  }
}

export async function backendPatch(endpoint, body, options = {}) {
  try {
    return await backendRequest(endpoint, {
      ...options,
      method: 'PATCH',
      body: JSON.stringify(body)
    });
  } catch (error) {
    if (error instanceof BackendError && error.isCritical && FEATURE_FLAGS.enableAutoFallback) {
      return await executeFallback(error, endpoint, body);
    }
    throw error;
  }
}

export async function backendPut(endpoint, body, options = {}) {
  try {
    return await backendRequest(endpoint, {
      ...options,
      method: 'PUT',
      body: JSON.stringify(body)
    });
  } catch (error) {
    if (error instanceof BackendError && error.isCritical && FEATURE_FLAGS.enableAutoFallback) {
      return await executeFallback(error, endpoint, body);
    }
    throw error;
  }
}

export async function backendDelete(endpoint, options = {}) {
  return backendRequest(endpoint, {
    ...options,
    method: 'DELETE'
  });
}


